import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { CheckCircle2, ListChecks, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AttentionList } from "@/components/looseend/AttentionList";
import { EmptyState } from "@/components/looseend/EmptyState";
import { HostedLoopsNotice } from "@/components/looseend/HostedLoopsNotice";
import { ResolutionQueueItem } from "@/components/looseend/ResolutionQueueItem";
import { useInspector } from "@/components/looseend/inspector-context";
import { draftFollowUp } from "@/lib/looseend/assistant";
import { queueLoops, statusCounts, whyNow } from "@/lib/looseend/selectors";
import { engineTally, surfacedBecause, tallySentence, PEE_ARIA } from "@/lib/looseend/pee";
import { ACTIVE_STATUSES, STATUS_META } from "@/lib/looseend/status";
import { store, useAppState } from "@/lib/looseend/store";
import { useHostedLoopActions, useHostedLoops } from "@/lib/loops/product/hostedLoops";
import { analytics } from "@/lib/analytics";
import type { LoopStatus } from "@/lib/looseend/types";

export const Route = createFileRoute("/app/queue")({
  head: () => ({
    meta: [
      { title: "Resolution Queue — L∞seEnd" },
      {
        name: "description",
        content:
          "Your unresolved Loops in recommended order, each with the next action and why it matters now.",
      },
      { property: "og:title", content: "Resolution Queue — L∞seEnd" },
      { property: "og:description", content: "One ordered list of what to resolve next." },
    ],
  }),
  component: QueuePage,
});

function QueuePage() {
  const state = useAppState();
  const { open } = useInspector();
  const [statusFilter, setStatusFilter] = useState<LoopStatus | "all">("all");
  const [counterpartyFilter, setCounterpartyFilter] = useState<string>("all");

  const hosted = useHostedLoops();
  const actions = useHostedLoopActions();
  const allLoops = useMemo(() => [...hosted.loops], [hosted.loops]);

  const counts = useMemo(() => statusCounts(allLoops), [allLoops]);
  const tally = useMemo(() => engineTally(counts), [counts]);
  const people = useMemo(
    () =>
      Array.from(new Set(allLoops.map((l) => l.counterparty).filter(Boolean) as string[])).sort(),
    [allLoops],
  );

  const loops = useMemo(() => {
    return queueLoops(allLoops).filter(
      (l) =>
        (statusFilter === "all" || l.status === statusFilter) &&
        (counterpartyFilter === "all" || l.counterparty === counterpartyFilter),
    );
  }, [allLoops, statusFilter, counterpartyFilter]);

  useEffect(() => {
    analytics.track("resolution_queue_opened", { interface_surface: "resolution_queue" });
  }, []);

  const takeAction = (loopId: string) => {
    const loop = allLoops.find((l) => l.id === loopId);
    if (!loop) return;
    analytics.track("queue_action_started", {
      interface_surface: "resolution_queue",
      loop_status: loop.status,
      action_type: loop.nextAction.kind,
    });
    if (loop.status === "ready_to_verify") {
      open(loop.id);
      toast.info("Confirm this is finished", {
        description: "Open “Finish it” to record what confirms the outcome.",
      });
      return;
    }
    if (loop.nextAction.kind === "send_reminder" || loop.nextAction.kind === "draft_message") {
      // Drafting stays local; no Loop state moves without the backend.
      void navigator.clipboard?.writeText(draftFollowUp(loop)).catch(() => undefined);
      toast.success("Follow-up drafted", { description: "Copied to your clipboard." });
      return;
    }
    open(loop.id);
  };

  return (
    <div className="h-full overflow-y-auto px-4 py-6 sm:px-8">
      <div className="mx-auto max-w-3xl">
        <header className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-4 sm:flex sm:flex-wrap sm:justify-between">
          <div className="min-w-0">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Resolution Queue
            </p>
            <h1 className="mt-1 text-2xl font-semibold tracking-tight text-foreground">
              What needs your attention
            </h1>
            <p className="mt-1.5 text-sm text-muted-foreground">
              {loops.length === 0
                ? "In the order it’s worth doing."
                : `${loops.length} item${loops.length === 1 ? "" : "s"}, in the order it’s worth doing.`}{" "}
              <span className="text-muted-foreground/80">
                Ordered by <span aria-label={PEE_ARIA}>PEE™</span>.
              </span>
            </p>
          </div>
        </header>

        <AttentionList onOpenLoop={(loopId) => open(loopId)} />

        {tally.total > 0 && !state.peeQueueSummaryDismissed ? (
          <div className="mt-4 flex items-start gap-2 rounded-xl border border-primary/20 bg-primary-soft px-3 py-2.5">
            <Sparkles className="mt-0.5 size-3.5 shrink-0 text-primary" aria-hidden="true" />
            <p className="min-w-0 flex-1 text-sm text-foreground">
              <span className="sr-only">{PEE_ARIA}: </span>
              {tallySentence(tally)}
            </p>
            <Button
              variant="ghost"
              size="icon"
              aria-label="Hide this summary"
              className="-mr-1 -mt-1 size-7 shrink-0"
              onClick={() => store.dismissQueueSummary()}
            >
              <X className="size-4" aria-hidden="true" />
            </Button>
          </div>
        ) : null}

        <div className="mt-5 flex flex-wrap gap-2">
          <Select
            value={statusFilter}
            onValueChange={(v) => setStatusFilter(v as LoopStatus | "all")}
          >
            <SelectTrigger className="w-[190px]" aria-label="Filter by status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {ACTIVE_STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {STATUS_META[s].label} ({counts[s]})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={counterpartyFilter} onValueChange={setCounterpartyFilter}>
            <SelectTrigger className="w-[190px]" aria-label="Filter by person">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Everyone</SelectItem>
              {people.map((person) => (
                <SelectItem key={person} value={person}>
                  {person}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {statusFilter !== "all" || counterpartyFilter !== "all" ? (
            <Button
              variant="ghost"
              onClick={() => {
                setStatusFilter("all");
                setCounterpartyFilter("all");
              }}
            >
              Clear filters
            </Button>
          ) : null}
        </div>

        <HostedLoopsNotice
          phase={hosted.phase}
          message={hosted.message}
          onRetry={hosted.refresh}
          rows={3}
        />

        {hosted.phase !== "ready" ? null : loops.length === 0 ? (
          <EmptyState
            className="mt-6"
            icon={
              allLoops.length ? (
                <CheckCircle2 className="size-6" aria-hidden="true" />
              ) : (
                <ListChecks className="size-6" aria-hidden="true" />
              )
            }
            title={
              allLoops.length
                ? "Nothing needs your attention right now."
                : "Nothing is being tracked yet."
            }
            description={
              allLoops.length
                ? "Everything here is either resolved or genuinely waiting on someone else."
                : "Tell LooseEnd what’s hanging over you and it will show up here."
            }
            action={
              allLoops.length ? undefined : (
                <Button asChild>
                  <Link to="/app">Tell LooseEnd what’s unresolved</Link>
                </Button>
              )
            }
          />
        ) : (
          <ul className="mt-5 space-y-3">
            {loops.map((loop) => (
              <ResolutionQueueItem
                key={loop.id}
                loop={loop}
                explanation={surfacedBecause(loop, allLoops, whyNow(loop, allLoops))}
                onInspect={() => open(loop.id)}
                onTakeAction={() => takeAction(loop.id)}
                onDraftFollowUp={() => {
                  void navigator.clipboard?.writeText(draftFollowUp(loop)).catch(() => undefined);
                  toast.success("Follow-up drafted", { description: "Copied to your clipboard." });
                }}
                onMarkWaiting={() => {
                  void actions.transition(loop, "waiting").then((result) => {
                    if (result.ok) toast.success("Marked as waiting");
                    else toast.error(result.message);
                  });
                }}
                onSnooze={(days) => {
                  const until = new Date(Date.now() + days * 86_400_000).toISOString();
                  void actions.snooze(loop, until).then((result) => {
                    if (result.ok) toast.success(`Snoozed for ${days} day${days === 1 ? "" : "s"}`);
                    else toast.error(result.message);
                  });
                }}
                onAddEvidence={() => open(loop.id)}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
